import { HardDrive, Loader2, Users } from 'lucide-react';
import type { ConnectionStatus } from './binding/useBoardDoc';

interface Props {
  status: ConnectionStatus;
  peerCount: number;
}

// Backendless re-interpretation of the original status pill:
//   loading — IndexedDB still replaying; show a spinner
//   ready   — board is loaded, no peers (solo); subtle "Solo" pill
//   live    — at least one peer connected; "Live · N peers" pill
export function ConnectionPill({ status, peerCount }: Props) {
  if (status === 'loading') {
    return (
      <Pill tone="muted" icon={<Loader2 size={12} strokeWidth={1.5} className="animate-spin" />}>
        Loading…
      </Pill>
    );
  }

  if (status === 'live') {
    const label = peerCount === 1 ? '1 peer' : `${peerCount} peers`;
    return (
      <Pill tone="success" icon={<Users size={12} strokeWidth={1.5} />}>
        Live · {label}
      </Pill>
    );
  }

  return (
    <Pill tone="muted" icon={<HardDrive size={12} strokeWidth={1.5} />}>
      Solo · saved locally
    </Pill>
  );
}

function Pill({
  tone,
  icon,
  children,
}: {
  tone: 'muted' | 'success';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const toneClasses =
    tone === 'success'
      ? 'bg-success-subtle text-success border-success'
      : 'bg-bg-panel text-fg-tertiary border-border-default';
  return (
    <div
      role="status"
      className={[
        'h-6 px-2.5 inline-flex items-center gap-1.5',
        'rounded-2 border-1.5',
        'font-mono text-12 tabular-nums',
        toneClasses,
      ].join(' ')}
    >
      {icon}
      {children}
    </div>
  );
}
